import React, { useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { Printer } from 'lucide-react';
import { biAPI } from '../../services/api';
import BiFilters from './BiFilters';
import { biPanel, formatNpr, formatNumber, formatPct } from './biTheme';
import {
  formatProbability,
  scoreDeltaSignal,
  scoreRateDelta,
  scoreUpliftSignal,
} from './biSignalStats';
import { useBiVendorId } from './useBiVendorId';
import useDebouncedValue from './useDebouncedValue';

const defaultTo = () => new Date().toISOString().slice(0, 10);
const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 29);
  return d.toISOString().slice(0, 10);
};

const SignalList = ({ title, tone, items }) => {
  const isGood = tone === 'good';
  return (
    <section
      className={`rounded-xl border p-4 print:break-inside-avoid ${
        isGood ? 'border-emerald-200 bg-emerald-50/70' : 'border-rose-200 bg-rose-50/70'
      }`}
    >
      <h3 className={`mb-3 text-base font-semibold uppercase tracking-wide ${
        isGood ? 'text-emerald-800' : 'text-rose-800'
      }`}
      >
        {title}
      </h3>
      {items.length ? (
        <ul className="space-y-3">
          {items.map((item) => (
            <li key={item.id} className={isGood ? 'text-emerald-950' : 'text-rose-950'}>
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold">{item.field}</p>
                {item.probability != null ? (
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold tabular-nums ${
                    isGood ? 'bg-emerald-200/70 text-emerald-900' : 'bg-rose-200/70 text-rose-900'
                  }`}
                  >
                    {formatProbability(item.probability)}
                  </span>
                ) : null}
              </div>
              <p className="text-base font-bold leading-snug">{item.headline}</p>
            </li>
          ))}
        </ul>
      ) : (
        <p className={`text-sm ${isGood ? 'text-emerald-800/70' : 'text-rose-800/70'}`}>—</p>
      )}
    </section>
  );
};

const BaReportGenerator = () => {
  const { vendorId, storeLabel } = useBiVendorId();
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const [weekdays, setWeekdays] = useState([]);
  const debouncedFrom = useDebouncedValue(from, 450);
  const debouncedTo = useDebouncedValue(to, 450);
  const debouncedWeekdays = useDebouncedValue(weekdays, 450);

  const queryParams = useMemo(() => ({
    vendorId,
    from: debouncedFrom,
    to: debouncedTo,
    weekdays: debouncedWeekdays.join(','),
  }), [vendorId, debouncedFrom, debouncedTo, debouncedWeekdays]);

  const queryOpts = {
    keepPreviousData: true,
    staleTime: 5 * 60 * 1000,
    cacheTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  };

  // Core-only: skip heavy overview extras + funnel device/timing charts.
  const overviewQuery = useQuery(
    ['bi-ba-overview', 'core', queryParams],
    async () => {
      const response = await biAPI.getOverview({
        ...queryParams,
        section: 'core',
        salesGrain: 'daily',
        salesMetric: 'totalSales',
      });
      return response.data?.data || response.data;
    },
    queryOpts
  );

  const funnelQuery = useQuery(
    ['bi-ba-funnel', 'core', queryParams],
    async () => {
      const response = await biAPI.getFunnel({
        ...queryParams,
        section: 'core',
        period: 'daily',
      });
      return response.data?.data || response.data;
    },
    queryOpts
  );

  const promoQuery = useQuery(
    ['bi-ba-promo', vendorId, debouncedFrom, debouncedTo],
    async () => {
      const response = await biAPI.getPromo({
        vendorId,
        from: debouncedFrom,
        to: debouncedTo,
        includeDaily: 0,
      });
      return response.data?.data || response.data;
    },
    queryOpts
  );

  const isLoading = overviewQuery.isLoading || funnelQuery.isLoading || promoQuery.isLoading;
  const isFetching = overviewQuery.isFetching || funnelQuery.isFetching || promoQuery.isFetching;
  const isError = overviewQuery.isError || funnelQuery.isError || promoQuery.isError;
  const error = overviewQuery.error || funnelQuery.error || promoQuery.error;

  const { good, bad, meta } = useMemo(() => {
    const overview = overviewQuery.data || {};
    const funnel = funnelQuery.data || {};
    const promo = promoQuery.data || {};
    const kpis = overview.kpis || {};
    const deltas = kpis.deltas || {};
    const summary = funnel.summary || {};
    const performance = funnel.performance || [];
    const stages = funnel.stages || [];
    const promoSummary = promo.summary || {};
    const promoProducts = promo.overallProducts || promo.products || [];
    const categoryShare = overview.categoryShare || [];
    const salesByProduct = overview.salesByProduct || [];
    const prev = overview.prevPeriod || funnel.summary?.prevPeriod || promo.prevPeriod || {};

    const goodItems = [];
    const badItems = [];
    const push = (list, id, field, headline, signal) => {
      list.push({
        id,
        field,
        headline,
        probability: signal?.probability ?? null,
      });
    };
    const route = (signal, id, field, headline) => {
      if (!signal || signal.strength === 'flat') return;
      push(signal.good ? goodItems : badItems, id, field, headline, signal);
    };

    const pickDelta = (obj) => (obj?.vsPrevPeriod != null ? obj.vsPrevPeriod : obj?.vsAvgPrev4);

    const salesDelta = pickDelta(deltas.netSales);
    const salesSignal = scoreDeltaSignal({ deltaPct: salesDelta, sampleSize: kpis.totalOrders, higherIsGood: true });
    if (salesSignal) {
      route(salesSignal, 'sales', 'Sales', `${formatNpr(kpis.netSales)} · ${formatPct(salesDelta)} vs prev`);
    }

    const ordersDelta = pickDelta(deltas.totalOrders);
    const ordersSignal = scoreDeltaSignal({ deltaPct: ordersDelta, sampleSize: kpis.totalOrders, higherIsGood: true });
    if (ordersSignal) {
      route(ordersSignal, 'orders', 'Orders', `${formatNumber(kpis.totalOrders)} · ${formatPct(ordersDelta)} vs prev`);
    }

    const fulfillDelta = pickDelta(deltas.fulfillmentRate);
    const fulfillSignal = scoreDeltaSignal({
      deltaPct: fulfillDelta,
      sampleSize: kpis.totalOrders,
      higherIsGood: true,
    });
    if (fulfillSignal) {
      route(
        fulfillSignal,
        'fulfill',
        'Fulfillment',
        `${formatPct(kpis.fulfillmentRate)} · ${formatPct(fulfillDelta)} vs prev`
      );
    }

    const cartDelta = pickDelta(deltas.cartConversion);
    const cartSignal = scoreDeltaSignal({
      deltaPct: cartDelta,
      sampleSize: kpis.placedOrders || kpis.totalOrders,
      higherIsGood: true,
    });
    if (cartSignal) {
      route(
        cartSignal,
        'cart',
        'View→order',
        `${formatPct(kpis.cartConversion)} · ${formatPct(cartDelta)} vs prev`
      );
    }

    const returning = Number(kpis.returningCustomers || 0);
    const neu = Number(kpis.newCustomers || 0);
    if (returning + neu > 0) {
      push(
        returning > 0 ? goodItems : badItems,
        'customers',
        'Customers',
        `${formatNumber(returning)} returning · ${formatNumber(neu)} new`,
        null
      );
    }

    if (categoryShare[0]) {
      const top = categoryShare[0];
      push(
        goodItems,
        'top-cat',
        'Top category',
        `${top.name} · ${formatPct(top.value)}`,
        scoreDeltaSignal({
          deltaPct: Number(top.value) || 0,
          sampleSize: top.metricValue || top.sales || 0,
          higherIsGood: true,
          minAbsDelta: 0,
        })
      );
    }

    if (salesByProduct[0]) {
      const top = salesByProduct[0];
      push(
        goodItems,
        'top-sku',
        'Top product',
        `${top.name} · ${formatNpr(top.sales || top.value || 0)}`,
        scoreUpliftSignal({
          actual: top.sales || top.value || 0,
          expected: salesByProduct[1]?.sales || salesByProduct[1]?.value || 0,
          sampleSize: kpis.totalOrders,
        })
      );
    }

    const crSignal = scoreDeltaSignal({
      deltaPct: summary.overallConversionDelta,
      sampleSize: summary.appDau,
      higherIsGood: true,
    });
    if (crSignal) {
      route(
        crSignal,
        'dau-cr',
        'DAU → orders',
        `${formatPct(summary.overallConversion, 2)} · ${formatPct(summary.overallConversionDelta)} vs prev · DAU ${formatNumber(summary.appDau || 0)}`
      );
    }

    const bounce = performance.find((p) => p.key === 'bounceRate');
    if (bounce) {
      const signal = scoreRateDelta({
        current: bounce.value,
        previous: bounce.prevValue,
        sampleSize: bounce.sampleSize || summary.appDau,
        higherIsGood: false,
      });
      if (signal && signal.strength !== 'flat') {
        route(
          signal,
          'bounce',
          'Bounce',
          `${bounce.valueLabel}${bounce.vsPrevPeriod != null ? ` · ${formatPct(bounce.vsPrevPeriod)} vs prev` : ''}`
        );
      }
    }

    const abandon = performance.find((p) => p.key === 'abandonmentRate');
    if (abandon) {
      const signal = scoreRateDelta({
        current: abandon.value,
        previous: abandon.prevValue,
        sampleSize: abandon.sampleSize || summary.ordersCreated,
        higherIsGood: false,
      });
      if (signal && signal.strength !== 'flat') {
        route(
          signal,
          'abandon',
          'Abandonment',
          `${abandon.valueLabel}${abandon.vsPrevPeriod != null ? ` · ${formatPct(abandon.vsPrevPeriod)} vs prev` : ''}`
        );
      }
    }

    const bottleneck = funnel.dropOffExplanation;
    if (bottleneck?.stageLabel) {
      push(
        badItems,
        'bottleneck',
        'Bottleneck',
        `${bottleneck.stageLabel} · −${formatPct(bottleneck.dropOffPct)}`,
        scoreDeltaSignal({
          deltaPct: -Math.abs(Number(bottleneck.dropOffPct) || 0),
          sampleSize: summary.appDau,
          higherIsGood: true,
        })
      );
    }

    stages
      .filter((s) => Number(s.count) === 0 && s.key !== 'visits')
      .slice(0, 2)
      .forEach((stage) => {
        push(
          badItems,
          `zero-${stage.key}`,
          stage.label,
          '0',
          scoreDeltaSignal({
            deltaPct: stage.vsPrevPeriod != null ? stage.vsPrevPeriod : -100,
            sampleSize: summary.appDau,
            higherIsGood: true,
            minAbsDelta: 0,
          })
        );
      });

    if (promoSummary.upliftOrders != null) {
      const signal = scoreUpliftSignal({
        actual: promoSummary.actualOrders,
        expected: promoSummary.prevPeriodOrders || promoSummary.expectedOrders,
        sampleSize: promoSummary.actualOrders,
      });
      if (signal && signal.strength !== 'flat') {
        route(
          signal,
          'promo',
          'Features/Offers',
          `${formatNumber(Math.round(promoSummary.upliftOrders))} orders · ${formatPct(promoSummary.upliftOrdersPct)} · ${formatNpr(promoSummary.upliftSales)}`
        );
      }
    }

    promoProducts
      .filter((p) => p.isLive)
      .map((p) => ({
        p,
        signal: scoreUpliftSignal({
          actual: p.actualOrders,
          expected: p.prevPeriodOrders || p.expectedOrders,
          sampleSize: p.actualOrders,
        }),
      }))
      .filter(({ signal }) => signal && signal.strength !== 'flat')
      .sort((a, b) => Math.abs(b.signal.deltaPct) - Math.abs(a.signal.deltaPct))
      .slice(0, 6)
      .forEach(({ p, signal }) => {
        push(
          signal.good ? goodItems : badItems,
          `live-${p.productId}`,
          p.productName,
          `${formatNumber(Math.round(p.upliftOrders))} · ${formatPct(p.upliftOrdersPct)}`,
          signal
        );
      });

    const byProb = (a, b) => (b.probability || 0) - (a.probability || 0);
    goodItems.sort(byProb);
    badItems.sort(byProb);

    return {
      good: goodItems,
      bad: badItems,
      meta: {
        startDate: overview.startDate || funnel.startDate || promo.startDate || debouncedFrom,
        endDate: overview.endDate || funnel.endDate || promo.endDate || debouncedTo,
        prevStart: prev.startDate || null,
        prevEnd: prev.endDate || null,
        goodCount: goodItems.length,
        badCount: badItems.length,
      },
    };
  }, [overviewQuery.data, funnelQuery.data, promoQuery.data, debouncedFrom, debouncedTo]);

  const refetchAll = () => {
    overviewQuery.refetch();
    funnelQuery.refetch();
    promoQuery.refetch();
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3 print:hidden">
        <div>
          <h2 className="text-xl font-bold text-gray-900">BA Report</h2>
          <p className="mt-1 text-sm font-medium text-sky-800">Store: {storeLabel}</p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <BiFilters
            inline
            from={from}
            to={to}
            onFromChange={setFrom}
            onToChange={setTo}
            weekdays={weekdays}
            onWeekdaysChange={setWeekdays}
            onRefresh={refetchAll}
            refreshing={isFetching}
          />
          <button type="button" onClick={() => window.print()} className="btn-primary inline-flex items-center gap-2 self-end">
            <Printer className="h-4 w-4" />
            Print / PDF
          </button>
        </div>
      </div>

      {isLoading ? <div className="card text-gray-500">Loading…</div> : null}
      {isError ? (
        <div className="card border-rose-200 bg-rose-50 text-rose-700">
          {error?.response?.data?.message || error?.message || 'Failed to load'}
        </div>
      ) : null}

      {!isLoading && !isError ? (
        <div id="ba-report-print" className="space-y-4">
          <div className={biPanel}>
            <p className="text-lg font-bold text-gray-900">
              {meta.startDate} → {meta.endDate}
            </p>
            {meta.prevStart ? (
              <p className="mt-1 text-sm text-slate-600">
                vs {meta.prevStart} → {meta.prevEnd}
              </p>
            ) : null}
            <p className="mt-1 text-sm text-slate-600">
              <span className="font-semibold text-emerald-700">{meta.goodCount}</span>
              {' / '}
              <span className="font-semibold text-rose-700">{meta.badCount}</span>
            </p>
          </div>

          <div className="grid gap-4 xl:grid-cols-2">
            <SignalList title="Green" tone="good" items={good} />
            <SignalList title="Red" tone="bad" items={bad} />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default BaReportGenerator;
